
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Link } from 'react-router-dom';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useAuth } from '@/hooks/useAuth';
import { useState } from 'react';
import { resetPassword } from '@/services/auth/resetPassword';

const loginSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginFormProps {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const LoginForm = ({ isLoading, setIsLoading }: LoginFormProps) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { toast } = useToast();
  const [resetInProgress, setResetInProgress] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    
    try {
      const { error } = await signIn(data.email, data.password);
      
      if (error) {
        // Handle specific error cases
        let errorMessage = error.message || "An error occurred during login";
        
        if (error.message?.includes('Invalid login credentials')) {
          errorMessage = "Invalid email or password. Please check your credentials and try again.";
        } else if (error.message?.includes('Email not confirmed')) {
          errorMessage = "Please check your email and click the confirmation link before logging in.";
        } else if (error.message?.includes('Too many requests')) {
          errorMessage = "Too many login attempts. Please wait a few minutes before trying again.";
        }
        
        toast({
          variant: "destructive",
          title: "Login failed",
          description: errorMessage,
        });
      } else {
        toast({
          title: "Login successful",
          description: "Welcome back!",
        });
        navigate('/');
      }
    } catch (error: any) {
      console.error('Unexpected login error:', error);
      toast({
        variant: "destructive",
        title: "Login failed",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const email = form.getValues('email');
    
    if (!email) {
      toast({
        variant: "destructive",
        title: "Email required",
        description: "Please enter your email address first",
      });
      form.setFocus('email');
      return;
    }
    
    // Validate email format
    const emailValidation = z.string().email().safeParse(email);
    if (!emailValidation.success) {
      toast({
        variant: "destructive",
        title: "Invalid email",
        description: "Please enter a valid email address",
      });
      form.setFocus('email');
      return;
    }
    
    try {
      setResetInProgress(true);
      
      const { error } = await resetPassword(email);
      
      if (error) {
        console.error('Password reset error:', error);
        let errorMessage = error.message || "An error occurred while sending the reset email";
        
        if (error.message?.includes('User not found')) {
          errorMessage = "No account found with this email address.";
        } else if (error.message?.includes('too many requests')) {
          errorMessage = "Too many reset requests. Please wait a few minutes before trying again.";
        }
        
        toast({
          variant: "destructive",
          title: "Password reset failed",
          description: errorMessage,
        });
      } else {
        setResetEmailSent(true);
        toast({
          title: "Password reset email sent",
          description: "Please check your inbox for instructions to reset your password. The link will expire in 1 hour.",
        });
      }
    } catch (error: any) {
      console.error('Unexpected password reset error:', error);
      toast({
        variant: "destructive",
        title: "Password reset failed",
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setResetInProgress(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input 
                  placeholder="email@example.com" 
                  type="email"
                  autoComplete="email"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>Password</FormLabel>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-xs font-normal" 
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resetInProgress || isLoading}
                >
                  {resetInProgress ? 'Sending...' : resetEmailSent ? 'Email Sent!' : 'Forgot Password?'}
                </Button>
              </div>
              <FormControl>
                <Input 
                  type="password" 
                  autoComplete="current-password"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {resetEmailSent && (
          <div className="text-sm text-muted-foreground bg-blue-50 p-3 rounded-md">
            <p>Password reset email sent! Please check your inbox and follow the instructions.</p>
          </div>
        )}
        
        <Button type="submit" className="w-full" disabled={isLoading || resetInProgress}>
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>
      </form>
    </Form>
  );
};

export default LoginForm;
